/**
 * Product combos — un producto de catálogo con precio propio compuesto por
 * otros productos. Los componentes gobiernan stock y producción de cocina;
 * el revenue queda en el producto padre.
 *
 * Distinto de menu_modifiers (elección del cliente) y promotions (regla de
 * precio): el combo es composición fija definida por el dueño.
 */

export type ProductComboComponent = {
  /** Producto componente (debe ser producto del catálogo del org). */
  productId: string;
  /** Cantidad de componente por unidad de combo. */
  qty: number;
  /** Orden estable para cocina y edición. */
  sortOrder: number;
  /** Nombre del producto (join de lectura). */
  name?: string;
};

export type ProductCombo = {
  productId: string;
  components: ProductComboComponent[];
  /** ISO timestamptz; null = sin límite (combo permanente). */
  validFrom: string | null;
  validUntil: string | null;
};

/** true cuando el producto tiene al menos un componente configurado. */
export function isComboProduct(combo: ProductCombo | null | undefined): boolean {
  return (combo?.components?.length ?? 0) > 0;
}

export type ComboAvailability = 'valid' | 'not_yet' | 'expired';

/**
 * Vigencia con bordes inclusivos — misma regla que el server
 * (`apply_sale_command_legacy_core` → combo_not_available).
 */
export function comboAvailability(
  combo: Pick<ProductCombo, 'validFrom' | 'validUntil'> | null | undefined,
  now: Date = new Date(),
): ComboAvailability {
  if (!combo) return 'valid';
  if (combo.validFrom && now < new Date(combo.validFrom)) return 'not_yet';
  if (combo.validUntil && now > new Date(combo.validUntil)) return 'expired';
  return 'valid';
}

/** Producto vendible hoy: mismo criterio con campos del catálogo/POS. */
export function isComboValidityOutOfWindow(
  p: { comboValidFrom?: string | null; comboValidUntil?: string | null } | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!p) return false;
  if (p.comboValidFrom && now < new Date(p.comboValidFrom)) return true;
  if (p.comboValidUntil && now > new Date(p.comboValidUntil)) return true;
  return false;
}

/** YYYY-MM-DD local → ISO o null; devuelve 'invalid' si el formato no sirve. */
export function parseComboDateInput(raw: string, endOfDay: boolean): string | null | 'invalid' {
  const t = raw.trim();
  if (!t) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return 'invalid';
  const d = new Date(`${t}T${endOfDay ? '23:59:59.999' : '00:00:00'}`);
  if (Number.isNaN(d.getTime())) return 'invalid';
  return d.toISOString();
}

export type ComboDraftComponent = {
  productId: string;
  qty: number;
};

/** Valida un draft antes de enviar al RPC; devuelve mensaje de error o null. */
export function validateComboDraft(
  comboProductId: string | null | undefined,
  components: ComboDraftComponent[],
): string | null {
  if (!comboProductId?.trim()) return 'combo_product_required';
  const seen = new Set<string>();
  for (const c of components) {
    const pid = c.productId?.trim();
    if (!pid) return 'combo_component_required';
    if (pid === comboProductId) return 'combo_cannot_contain_itself';
    if (seen.has(pid)) return 'combo_component_duplicated';
    seen.add(pid);
    if (!Number.isFinite(c.qty) || c.qty <= 0) return 'combo_component_qty_invalid';
  }
  return null;
}

/** Agrupa las filas del RPC list_product_combos por producto padre. */
export function groupProductComboRows(
  rows: Array<{
    combo_product_id: string;
    component_product_id: string;
    qty: number;
    sort_order: number;
    component_name?: string | null;
    combo_valid_from?: string | null;
    combo_valid_until?: string | null;
  }>,
): Map<string, ProductCombo> {
  const byCombo = new Map<string, ProductCombo>();
  for (const row of rows) {
    let combo = byCombo.get(row.combo_product_id);
    if (!combo) {
      combo = {
        productId: row.combo_product_id,
        components: [],
        validFrom: row.combo_valid_from ?? null,
        validUntil: row.combo_valid_until ?? null,
      };
      byCombo.set(row.combo_product_id, combo);
    }
    combo.components.push({
      productId: row.component_product_id,
      qty: Number(row.qty) || 1,
      sortOrder: Number(row.sort_order) || 0,
      name: row.component_name ?? undefined,
    });
  }
  for (const combo of byCombo.values()) {
    combo.components.sort((a, b) => a.sortOrder - b.sortOrder);
  }
  return byCombo;
}
